import type { SimplifyOpts } from '@elara/core/introspection';

import { isSignal } from '@preact-signals/utils';
import JsonView from '@uiw/react-json-view';

import { deepValueMap } from '@elara/client-core/lib/utils';
import { objectKey, simplify } from '@elara/core/introspection';
import { isFn } from '@elara/core/types';

import { PrettyPre } from '#ui/debug/pretty.js';

export function JsonDump({ data, simplified }: { data: unknown; simplified?: true | SimplifyOpts }) {
  let jsonBody = getCustomJsonDump(data) ?? data;
  if (jsonBody === undefined) {
    return <PrettyPre value={'undefined'} />;
  }
  if (simplified) {
    const baseOpts: SimplifyOpts = {
      maxDepth: 10,
      maxStringLength: 1024,
      omitKeys: [],
      asIsKeys: ['agent', 'input', 'output', 'error'],
    };
    jsonBody = simplified === true ? simplify(jsonBody, baseOpts) : simplify(jsonBody, { ...baseOpts, ...simplified });
  }
  if (typeof jsonBody === 'object' && jsonBody !== null) {
    const cleanedJsonBody = deepValueMap(
      jsonBody,
      (value) => {
        if (isFn(value)) return;
        if (isSignal(value)) return value.peek();
        return value;
      },
      { compact: true },
    );
    return (
      <div className="text-xs rounded overflow-x-auto">
        <JsonView
          value={cleanedJsonBody}
          shortenTextAfterLength={128}
          displayDataTypes={false}
          displayObjectSize={false}
          shouldExpandNodeInitially={(_isExpanded, opts) => opts.parentValue === undefined}
        >
          <JsonView.Quote render={() => <span />} />
          <JsonView.Undefined render={(props) => <span {...props}>undefined</span>} />
          <JsonView.String render={(props, { value }) => <span {...props}>{value as string}</span>} />
        </JsonView>
      </div>
    );
  } else {
    return <PrettyPre value={typeof jsonBody === 'string' ? jsonBody : JSON.stringify(jsonBody, null, 2)} />;
  }
}

function getCustomJsonDump(data: unknown, { keys = ['asJsonDump', 'toJsonDump', 'toJSON'] } = {}): unknown {
  if (typeof data !== 'object' || data === null) return;
  const candidates = keys.map((key) => objectKey(key).getFrom(data));
  const candidate = candidates.find((v) => v !== undefined);
  if (candidate === undefined) return;
  if (isFn(candidate)) return candidate();
  return candidate;
}
